import { handleAppError } from '@/lib/errorUtils';
import { useState, useCallback } from 'react';
import { api } from '@/api/client';
import { Resume } from '@/api/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function useResumeLibrary(workspaceId: string) {
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(() => {
    return localStorage.getItem(`resumeChecker_selectedResumeId_${workspaceId}`) || null;
  });

  const {
    data,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['resumes', workspaceId],
    queryFn: async () => {
      const response = await api.getResumes(workspaceId);
      return response.items;
    },
  });

  const resumes = data || [];
  const error = queryError ? handleAppError(queryError, 'Failed to open resume list.') : null;

  const handleSelect = useCallback((id: string | null) => {
    setSelectedId(id);
    if (id) {
      localStorage.setItem(`resumeChecker_selectedResumeId_${workspaceId}`, id);
    } else {
      localStorage.removeItem(`resumeChecker_selectedResumeId_${workspaceId}`);
    }
  }, [workspaceId]);

  const validatedSelectedId = (resumes.length > 0 && selectedId && !resumes.find((r) => r.id === selectedId)) 
    ? null 
    : selectedId;

  const uploadMutation = useMutation({
    mutationFn: async ({ fileContentBase64, name, label }: { fileContentBase64: string, name: string, label: string }) => {
      return await api.uploadResume(workspaceId, fileContentBase64, name, label);
    },
    onSuccess: (newResume) => {
      queryClient.setQueryData(['resumes', workspaceId], (oldData: Resume[] | undefined) => {
        return oldData ? [...oldData, newResume] : [newResume];
      });
      handleSelect(newResume.id);
    },
    onError: (err) => {
      handleAppError(err, 'Upload failed.');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.deleteResume(workspaceId, id);
      return id;
    },
    onSuccess: (id) => {
      queryClient.setQueryData(['resumes', workspaceId], (oldData: Resume[] | undefined) => {
        return oldData ? oldData.filter((r) => r.id !== id) : [];
      });
      if (id === selectedId) {
        handleSelect(null);
      }
    },
    onError: (err) => {
      handleAppError(err, 'Delete failed.');
    }
  });

  const uploadResume = useCallback((fileContentBase64: string, name: string, label: string) => {
    return uploadMutation.mutateAsync({ fileContentBase64, name, label });
  }, [uploadMutation]);

  const deleteResume = useCallback((id: string) => {
    return deleteMutation.mutateAsync(id);
  }, [deleteMutation]);

  return {
    resumes,
    selectedId: validatedSelectedId,
    loading,
    error,
    selectResume: handleSelect,
    uploadResume,
    deleteResume,
    refresh: refetch,
  };
}
